import {miniSerializeError, SerializedError} from '@reduxjs/toolkit';
import {ClientError} from 'graphql-request';
import {t} from '../common/getTranslations';
import {PagewebError} from './PagewebError.class';
import {PagewebErrorType} from './PagewebErrorType.enum';
import {SerializedPagewebError} from './SerializedPagewebError.interface';

export function getSerializedPagewebError(
  error:
    | SerializedError
    | PagewebError
    | SerializedPagewebError
    | Error
    | ClientError
    | string
): SerializedPagewebError {
  if (error instanceof PagewebError) {
    return error.serializedError;
  } else if (typeof error === 'object' && 'isSerializedPagewebError' in error) {
    return error as SerializedPagewebError;
  } else if (error instanceof ClientError) {
    return withGraphqlRequestError(error);
  } else {
    return withError(error);
  }
}

function withGraphqlRequestError(error: ClientError): SerializedPagewebError {
  if (error.response.errors) {
    const graphqlError = error.response.errors[0];
    if ('isSerializedPagewebError' in graphqlError.extensions) {
      return graphqlError.extensions as SerializedPagewebError;
    } else if (graphqlError.extensions.response?.status >= 300) {
      return {
        isSerializedPagewebError: true,
        type: PagewebErrorType.ApiResponseFailError,
        status: error.response.status,
        message: error.message,
        originalMessage: graphqlError.extensions.originalMessage,
      };
    } else {
      return {
        isSerializedPagewebError: true,
        type: PagewebErrorType.ApolloError,
        apolloErrorCode: graphqlError.extensions.code,
        message: graphqlError.message,
        originalMessage: graphqlError.extensions.originalMessage,
      };
    }
  }
  // apollo-client에서 network error 를 300~ status로 구분하여 처리하고 있어 이를 참고했습니다.
  // https://www.apollographql.com/docs/react/data/error-handling/#network-errors
  if (error.response.status && error.response.status >= 300) {
    return {
      isSerializedPagewebError: true,
      type: PagewebErrorType.ApiResponseFailError,
      status: error.response.status,
      message: error.message,
    };
  }
  return {
    isSerializedPagewebError: true,
    type: PagewebErrorType.UnexpectedFetchError,
    message: t('api_common_fail'),
  };
}

function withError(
  error: Error | SerializedError | string
): SerializedPagewebError {
  const serializedError = miniSerializeError(error);

  return {
    isSerializedPagewebError: true,
    type: PagewebErrorType.UnknownError,
    message: serializedError.message || t('api_common_fail'),
    stack: serializedError.stack,
  };
}
